import { useState, useEffect, useRef } from "react";
import { Modal, Select } from "shared/components";
import { DateInput } from "shared/components/forms/DateInput";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import DatePickerComp from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "./formatDate";

export interface DateRangePreset {
  value: string;
  label: string;
  icon: IconSvgElement;
  isNone?: boolean;
  isCustom?: boolean;
  getDateRange?: () => { startDate: string; endDate: string };
}

interface DateRangeSelectProps {
  id: string;
  label: string;
  value: string;
  startDate?: string;
  endDate?: string;
  presets: DateRangePreset[];
  onChange: (presetValue: string, startDate?: string, endDate?: string) => void;
  modalZIndex?: number;
}

const parseDate = (str: string): Date => new Date(str + "T00:00:00");

export const DateRangeSelect = ({
  id,
  label,
  value,
  startDate,
  endDate,
  presets,
  onChange,
  modalZIndex = 200
}: DateRangeSelectProps) => {
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const [openCustom, setOpenCustom] = useState(false);
  const [pickerDates, setPickerDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: startDate ? parseDate(startDate) : null,
    endDate: endDate ? parseDate(endDate) : null
  });

  useEffect(() => {
    setPickerDates({
      startDate: startDate ? parseDate(startDate) : null,
      endDate: endDate ? parseDate(endDate) : null
    });
  }, [startDate, endDate]);

  const customPreset = presets.find((p) => p.isCustom);
  const isCustomSelected = customPreset && value === customPreset.value;

  const options = presets.map((preset) => ({
    value: preset.value,
    label: preset.label,
    icon: preset.icon,
    isNone: preset.isNone
  }));

  const handleSelectChange = (e: { target: { value: string } }) => {
    const selectedValue = e.target.value;
    const preset = presets.find((p) => p.value === selectedValue);
    if (!preset) return;

    if (preset.isCustom) {
      setOpenCustom(true);
      return;
    }

    if (preset.getDateRange) {
      const { startDate: s, endDate: e } = preset.getDateRange();
      onChange(selectedValue, s, e);
    } else onChange(selectedValue, undefined, undefined);
  };

  const handleDatePickerChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setPickerDates({ startDate: start, endDate: end });

    if (start && end && customPreset) {
      onChange(customPreset.value, formatDate(start), formatDate(end));
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => setOpenCustom(false), 300);
    }
  };

  const handleStartDateInput = (dateStr: string) => {
    if (dateStr && dateStr.length === 10) {
      setPickerDates((prev) => ({
        ...prev,
        startDate: parseDate(dateStr)
      }));
      if (customPreset && endDate)
        onChange(customPreset.value, dateStr, endDate);
    }
  };

  const handleEndDateInput = (dateStr: string) => {
    if (dateStr && dateStr.length === 10) {
      setPickerDates((prev) => ({
        ...prev,
        endDate: parseDate(dateStr)
      }));
      if (customPreset && startDate)
        onChange(customPreset.value, startDate, dateStr);
    }
  };

  return (
    <div>
      <Select
        id={id}
        label={label}
        labelClassName="text-content-1000 text-sm font-bold"
        hideOptional
        placeholder="Select date range..."
        value={value}
        options={options}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: modalZIndex }),
          option: (base, state) => {
            let bg = "#ffffff";
            if (state.isFocused) bg = "#1C1D221A";
            else if (state.isSelected) bg = "transparent";
            return { ...base, backgroundColor: bg };
          }
        }}
        formatOptionLabel={(option, { context }) => {
          const opt = option as (typeof options)[number];
          const preset = presets.find((p) => p.value === opt.value);

          let displayLabel: string = opt.label;
          if (context === "value" && isCustomSelected && startDate && endDate)
            displayLabel =
              startDate === endDate ? startDate : `${startDate} - ${endDate}`;

          return (
            <div className="flex items-center gap-3">
              {preset ? (
                <HugeiconsIcon
                  icon={preset.icon}
                  size={20}
                  strokeWidth={1.5}
                  className={
                    preset.isNone ? "text-content-200" : "text-content-1000"
                  }
                />
              ) : null}
              <span
                className={`text-base font-medium ${
                  preset?.isNone ? "text-content-200" : "text-content-1000"
                }`}
              >
                {displayLabel}
              </span>
            </div>
          );
        }}
        onChange={handleSelectChange}
      />

      <Modal
        opened={openCustom}
        onClose={() => setOpenCustom(false)}
        panelClassName="h-[470px] w-[400px] lg:w-[1000px]"
        zIndex={modalZIndex}
      >
        <div className="w-full">
          <div className="flex gap-[14px] border-b border-alpha-40 lg:items-center lg:flex-row flex-col px-6 py-4">
            <DateInput
              id={`${id}_start`}
              hideOptional
              label="From"
              value={startDate ?? ""}
              onChange={(e) => handleStartDateInput(e.target.value)}
            />
            <p className="hidden lg:block">-</p>
            <DateInput
              id={`${id}_end`}
              hideOptional
              label="To"
              value={endDate ?? ""}
              onChange={(e) => handleEndDateInput(e.target.value)}
            />
          </div>
          <div className="lg:block hidden">
            <DatePickerComp
              selected={pickerDates.startDate}
              onChange={handleDatePickerChange}
              monthsShown={2}
              startDate={pickerDates.startDate}
              endDate={pickerDates.endDate}
              selectsRange
              inline
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
